import type { KyInstance } from 'ky';

import { ensureError, formatString, isNotDefined } from '@enterprise/core';
import type { OperationResult } from '@enterprise/types';

import { Uri } from '#constants/uri';

/**
 * Deletes a mailing list for a given user.
 *
 * @param instance - The Ky HTTP client instance used to make the request.
 * @param mailingListId - The ID of the mailing list to delete.
 * @param userId - The ID of the user performing the deletion.
 * @returns A promise that resolves to the operation result of the deletion.
 * @throws Will throw an error if either `mailingListId` or `userId` is not provided, or if the HTTP request fails.
 */
export const deleteMailingList = async (instance: KyInstance, mailingListId?: number, userId?: number) => {
  try {
    if (isNotDefined(mailingListId) || isNotDefined(userId)) {
      throw new Error('Mailing List ID and User ID must be provided to delete a mailing list.');
    }

    return await instance
      .delete<OperationResult>(
        formatString(Uri.mailingList.deleteMailingList, mailingListId.toString(), userId.toString())
      )
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
